<template>
  <div style="background: #f8fafc; min-height: 100vh; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
    <ServicesServiceHero
      title="Platform Maintenance"
      description="Keep your system running smoothly with proactive maintenance and infrastructure management"
      icon="🛠️"
      gradient="platform-maintenance"
    />

    <!-- Main Content -->
    <div class="container" style="max-width: 1000px;">
      <div class="row">
        <div class="col-md-12">
          <div class="service-main-card">
            <h2 style="color: #1a202c; font-weight: 700; margin-bottom: 24px; font-size: 2.2rem;">Platform Maintenance Plans</h2>
            <p style="color: #4a5568; line-height: 1.7; font-size: 1.1rem; margin-bottom: 40px;">Maintenance plans help keep your system running smoothly and major upgrades are kept to minimum. Making small changes over time ensures consistency and reliability, and quick rollouts. Pricing scales based on your infrastructure size and complexity.</p>
            
            <!-- Infrastructure Size Guide -->
            <div style="background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); padding: 28px; border-radius: 12px; margin-bottom: 40px;">
              <h3 style="color: #1a202c; font-weight: 600; margin-bottom: 20px; font-size: 1.4rem;">📊 Understanding Infrastructure Sizes</h3>
              <div class="row">
                <div class="col-md-4 mb-3">
                  <div style="background: white; padding: 18px; border-radius: 10px; height: 100%; border-left: 4px solid #3b82f6;">
                    <h4 style="color: #3b82f6; font-weight: 600; margin-bottom: 10px; font-size: 1.1rem;">Small</h4>
                    <ul style="color: #4a5568; font-size: 0.9rem; margin: 0; padding-left: 20px; line-height: 1.7;">
                      <li>1-2 environments</li>
                      <li>5-10 AWS services</li>
                      <li>Single region</li>
                      <li>Basic monitoring</li>
                    </ul>
                  </div>
                </div>
                <div class="col-md-4 mb-3">
                  <div style="background: white; padding: 18px; border-radius: 10px; height: 100%; border-left: 4px solid #3b82f6;">
                    <h4 style="color: #3b82f6; font-weight: 600; margin-bottom: 10px; font-size: 1.1rem;">Medium</h4>
                    <ul style="color: #4a5568; font-size: 0.9rem; margin: 0; padding-left: 20px; line-height: 1.7;">
                      <li>3-4 environments</li>
                      <li>10-25 AWS services</li>
                      <li>Multi-region or HA</li>
                      <li>Advanced monitoring</li>
                    </ul>
                  </div>
                </div>
                <div class="col-md-4 mb-3">
                  <div style="background: white; padding: 18px; border-radius: 10px; height: 100%; border-left: 4px solid #3b82f6;">
                    <h4 style="color: #3b82f6; font-weight: 600; margin-bottom: 10px; font-size: 1.1rem;">Large/Enterprise</h4>
                    <ul style="color: #4a5568; font-size: 0.9rem; margin: 0; padding-left: 20px; line-height: 1.7;">
                      <li>5+ environments</li>
                      <li>25+ AWS services</li>
                      <li>Multi-region with DR</li>
                      <li>Complex monitoring</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pay As You Go -->
            <div style="background: #fff8f0; border-left: 4px solid #f59e0b; padding: 24px; border-radius: 12px; margin-bottom: 30px;">
              <h3 style="color: #1a202c; font-weight: 600; margin-bottom: 16px; font-size: 1.6rem;">💳 Pay-As-You-Go — $225/hr</h3>
              <p style="color: #4a5568; line-height: 1.7; margin-bottom: 16px;">Without a maintenance plan, all work is billed at our standard hourly rate with no commitment required.</p>
              <ul style="color: #4a5568; line-height: 1.7; margin-bottom: 0;">
                <li>Prior authorization required for all maintenance work</li>
                <li>Overhauls, changes, and upgrades will be required based on service changes in AWS</li>
                <li>Failure to do upgrades can increase hosting fees based on AWS support fees</li>
                <li>Example: MySQL 8.0 End of Life - Extended support kicks in August 1, 2026 (<a href="https://www.geekcafe.com/blog/2025/10/mysql-8-end-of-life-what-aws-forgot-to-tell-you" target="_blank" style="color: #3b82f6;">Learn more</a>)</li>
              </ul>
            </div>

            <p style="color: #4a5568; font-size: 1.1rem; margin-bottom: 30px; font-style: italic; text-align: center;">Or commit to a maintenance tier for significantly reduced hourly rates:</p>

            <!-- Pricing Tiers -->
            <div class="row">
              <!-- Tier 1: Essential Care -->
              <div class="col-md-12 mb-4">
                <div :style="{ background: 'linear-gradient(135deg, var(--platform-maintenance-start) 0%, var(--platform-maintenance-end) 100%)', color: 'white', padding: '32px', borderRadius: '16px' }">
                  <div style="display: flex; justify-content: space-between; align-items: start; flex-wrap: wrap; gap: 16px;">
                    <div>
                      <h4 style="font-weight: 700; margin-bottom: 8px; font-size: 1.5rem;">Tier 1: Essential Care</h4>
                      <p style="opacity: 0.9; margin: 0; font-size: 0.95rem;">Light maintenance for smaller infrastructures</p>
                    </div>
                    <div style="background: rgba(255,255,255,0.2); padding: 16px 24px; border-radius: 12px; backdrop-filter: blur(10px);">
                      <div style="font-size: 2rem; font-weight: 800;">$165-$200</div>
                      <div style="opacity: 0.9; font-size: 0.9rem;">per hour</div>
                    </div>
                  </div>
                  <div style="border-top: 1px solid rgba(255,255,255,0.2); margin: 20px 0;"></div>
                  <div style="background: rgba(255,255,255,0.15); padding: 16px; border-radius: 8px; margin-bottom: 20px;">
                    <strong>Monthly Commitment:</strong> 6-10 hours/month minimum<br />
                    <span style="opacity: 0.9; font-size: 0.9rem;">Rate varies based on infrastructure size</span>
                  </div>
                  <ul style="margin: 0; padding-left: 20px; line-height: 1.9;">
                    <li>IaC patching and updates</li>
                    <li>General AWS housekeeping</li>
                    <li><strong>Quarterly update review</strong> - System performance overview</li>
                    <li>Hosting architecture suggestions and resource optimization</li>
                    <li>Backup and restore strategy planning</li>
                    <li>Disaster & Recovery strategy development</li>
                    <li><strong>Discounted emergency rate</strong> for urgent issues</li>
                  </ul>
                  <p style="margin-top: 16px; opacity: 0.85; font-size: 0.9rem; font-style: italic;">Best for: Small infrastructures needing periodic updates and strategic guidance.</p>
                </div>
              </div>

              <!-- Tier 2: Standard Care (Recommended) -->
              <div class="col-md-12 mb-4">
                <div style="position: relative;">
                  <div style="position: absolute; top: -12px; right: 20px; background: #10b981; color: white; padding: 6px 16px; border-radius: 20px; font-weight: 600; font-size: 0.85rem; box-shadow: 0 2px 8px rgba(16,185,129,0.3);">RECOMMENDED</div>
                  <div :style="{ background: 'linear-gradient(135deg, var(--platform-maintenance-start) 0%, var(--platform-maintenance-end) 100%)', color: 'white', padding: '32px', borderRadius: '16px', border: '3px solid #10b981' }">
                    <div style="display: flex; justify-content: space-between; align-items: start; flex-wrap: wrap; gap: 16px;">
                      <div>
                        <h4 style="font-weight: 700; margin-bottom: 8px; font-size: 1.5rem;">Tier 2: Standard Care</h4>
                        <p style="opacity: 0.9; margin: 0; font-size: 0.95rem;">Comprehensive maintenance for growing infrastructures</p>
                      </div>
                      <div style="background: rgba(255,255,255,0.2); padding: 16px 24px; border-radius: 12px; backdrop-filter: blur(10px);">
                        <div style="font-size: 2rem; font-weight: 800;">$150-$175</div>
                        <div style="opacity: 0.9; font-size: 0.9rem;">per hour</div>
                      </div>
                    </div>
                    <div style="border-top: 1px solid rgba(255,255,255,0.2); margin: 20px 0;"></div>
                    <div style="background: rgba(255,255,255,0.15); padding: 16px; border-radius: 8px; margin-bottom: 20px;">
                      <strong>Monthly Commitment:</strong> 12-20 hours/month minimum<br>
                      <span style="opacity: 0.9; font-size: 0.9rem;">Rate varies based on infrastructure size</span>
                    </div>
                    <p style="margin-bottom: 12px; font-weight: 600;">Everything in Tier 1, plus:</p>
                    <ul style="margin: 0; padding-left: 20px; line-height: 1.9;">
                      <li><strong>Monthly AWS updates</strong> + dependency and pipeline updates</li>
                      <li>ECS/ASG tuning (optimizing traffic response and scaling)</li>
                      <li><strong>MySQL version migration (8.0 → 8.4) included</strong></li>
                      <li>Security and architecture best-practice checks</li>
                      <li>Monitoring & Alert notification setup</li>
                      <li>Health page implementation (system disruption awareness)</li>
                      <li>Playbooks / Runbooks (disaster response procedures)</li>
                      <li><strong>Emergency response</strong> with reduced hourly rate</li>
                    </ul>
                    <p style="margin-top: 16px; opacity: 0.85; font-size: 0.9rem; font-style: italic;">Best for: Medium infrastructures requiring regular updates, monitoring, and proactive maintenance.</p>
                    <div style="background: rgba(255,255,255,0.15); padding: 16px; border-radius: 8px; margin-top: 16px;">
                      <strong>Optional Add-ons:</strong>
                      <ul style="margin: 8px 0 0 0; padding-left: 20px;">
                        <li>Centralized Docs and Task Management (Jira & Confluence - subject to license fees, or GitHub Docs Repo and Projects - free)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tier 3: Fully Managed -->
              <div class="col-md-12 mb-4">
                <div :style="{ background: 'linear-gradient(135deg, var(--platform-maintenance-start) 0%, var(--platform-maintenance-end) 100%)', color: 'white', padding: '32px', borderRadius: '16px' }">
                  <div style="display: flex; justify-content: space-between; align-items: start; flex-wrap: wrap; gap: 16px;">
                    <div>
                      <h4 style="font-weight: 700; margin-bottom: 8px; font-size: 1.5rem;">Tier 3: Fully Managed</h4>
                      <p style="opacity: 0.9; margin: 0; font-size: 0.95rem;">Complete, proactive DevOps partnership</p>
                    </div>
                    <div style="background: rgba(255,255,255,0.2); padding: 16px 24px; border-radius: 12px; backdrop-filter: blur(10px);">
                      <div style="font-size: 2rem; font-weight: 800;">$135-$150</div>
                      <div style="opacity: 0.9; font-size: 0.9rem;">per hour</div>
                    </div>
                  </div>
                  <div style="border-top: 1px solid rgba(255,255,255,0.2); margin: 20px 0;"></div>
                  <div style="background: rgba(255,255,255,0.15); padding: 16px; border-radius: 8px; margin-bottom: 20px;">
                    <strong>Monthly Commitment:</strong> 20-40 hours/month minimum<br>
                    <span style="opacity: 0.9; font-size: 0.9rem;">Rate varies based on infrastructure size and complexity</span>
                  </div>
                  <p style="margin-bottom: 12px; font-weight: 600;">Everything in Tier 2, plus:</p>
                  <ul style="margin: 0; padding-left: 20px; line-height: 1.9;">
                    <li><strong>Comprehensive infrastructure management</strong> with proactive monitoring</li>
                    <li><strong>Priority emergency response</strong> with fastest response times</li>
                    <li>Load/performance testing, prep, and optimization</li>
                    <li>Annual architecture review and roadmap planning</li>
                    <li>Cost optimization analysis and recommendations</li>
                    <li><strong>Fast Re-Deployment</strong> capabilities for AWS AZ or regional outages</li>
                    <li><strong>Fast Failover</strong> capabilities for AWS AZ or regional outages, including automated switchover and rollback for critical services</li>
                    <li><strong>Always On Multi-Region</strong> with automatic load balancing and geo-replication for zero-downtime operations across multiple AWS regions.</li>
                    <li>Disaster recovery planning and testing</li>
                    <li>Security auditing and compliance monitoring</li>
                    <li>Advanced performance tuning and optimization</li>
                    <li>Continuous security monitoring and threat assessment</li>
                    <li>Proactive vulnerability management and patching</li>
                    <li>Comprehensive security hardening and configuration reviews</li>
                    <li>Dedicated support with regular strategic planning sessions</li>
                  </ul>
                  <p style="margin-top: 16px; opacity: 0.85; font-size: 0.9rem; font-style: italic;">Best for: Large/Enterprise infrastructures requiring complete DevOps partnership and high availability guarantees.</p>                  
                </div>
              </div>
            </div>

            <!-- Pricing Examples -->
            <div style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); padding: 32px; border-radius: 16px; margin-top: 40px; border-left: 4px solid #f59e0b;">
              <h3 style="color: #92400e; font-weight: 600; margin-bottom: 20px; font-size: 1.5rem;">💰 Pricing Examples</h3>
              <p style="color: #78350f; margin-bottom: 20px; line-height: 1.7;">Here are sample monthly costs to help you understand how infrastructure size impacts pricing:</p>
              
              <div class="row">
                <div class="col-md-4 mb-3">
                  <div style="background: white; padding: 20px; border-radius: 10px; height: 100%;">
                    <h4 style="color: #f59e0b; font-weight: 600; margin-bottom: 12px; font-size: 1.1rem;">Small Infrastructure</h4>
                    <div style="color: #4a5568; font-size: 0.9rem; line-height: 1.7;">
                      <strong>Tier 1 Example:</strong><br>
                      8 hrs × $165/hr = <strong>$1,320/mo</strong><br><br>
                      <strong>Tier 2 Example:</strong><br>
                      15 hrs × $150/hr = <strong>$2,250/mo</strong>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 mb-3">
                  <div style="background: white; padding: 20px; border-radius: 10px; height: 100%;">
                    <h4 style="color: #f59e0b; font-weight: 600; margin-bottom: 12px; font-size: 1.1rem;">Medium Infrastructure</h4>
                    <div style="color: #4a5568; font-size: 0.9rem; line-height: 1.7;">
                      <strong>Tier 1 Example:</strong><br>
                      10 hrs × $185/hr = <strong>$1,850/mo</strong><br><br>
                      <strong>Tier 2 Example:</strong><br>
                      18 hrs × $165/hr = <strong>$2,970/mo</strong>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 mb-3">
                  <div style="background: white; padding: 20px; border-radius: 10px; height: 100%;">
                    <h4 style="color: #f59e0b; font-weight: 600; margin-bottom: 12px; font-size: 1.1rem;">Large Infrastructure</h4>
                    <div style="color: #4a5568; font-size: 0.9rem; line-height: 1.7;">
                      <strong>Tier 2 Example:</strong><br>
                      20 hrs × $175/hr = <strong>$3,500/mo</strong><br><br>
                      <strong>Tier 3 Example:</strong><br>
                      30 hrs × $145/hr = <strong>$4,350/mo</strong>
                    </div>
                  </div>
                </div>
              </div>
              <p style="color: #78350f; margin-top: 16px; margin-bottom: 0; font-style: italic; font-size: 0.95rem;">These are examples only. Your actual pricing will be determined after an infrastructure assessment.</p>
            </div>

            <!-- Important Notes -->
            <div style="background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); padding: 32px; border-radius: 16px; margin-top: 40px; border-left: 4px solid #3b82f6;">
              <h3 style="color: #1a202c; font-weight: 600; margin-bottom: 20px; font-size: 1.5rem;">📋 Important Information</h3>
              <div style="color: #4a5568; line-height: 1.8;">
                <p style="margin-bottom: 12px;"><strong>Hourly rates are determined by:</strong></p>
                <ul style="margin-bottom: 20px;">
                  <li>Size and scope of your infrastructure</li>
                  <li>Number of environments and AWS services</li>
                  <li>Complexity of your architecture and codebase</li>
                  <li>Current state and technical debt</li>
                  <li>Geographic distribution and redundancy requirements</li>
                </ul>
                <p style="margin-bottom: 12px;"><strong>What's NOT included in maintenance plans:</strong></p>
                <ul style="margin-bottom: 20px;">
                  <li>Application feature development and enhancements</li>
                  <li>UI/UX redesigns or major updates</li>
                  <li>New workflow implementations</li>
                  <li>Third-party integrations requiring custom development</li>
                </ul>
                <p style="margin: 0; font-style: italic;">For development work, see our <NuxtLink to="/services/development-services" style="color: #3b82f6; text-decoration: underline;">Development & Feature Work</NuxtLink> service offerings.</p>
              </div>
            </div>

            <!-- CTA Section -->
            <div :style="{ background: `linear-gradient(135deg, var(--platform-maintenance-start) 0%, var(--platform-maintenance-end) 100%)`, color: 'white', padding: '40px', borderRadius: '16px', marginTop: '50px', textAlign: 'center' }">
              <h3 style="font-weight: 700; margin-bottom: 20px; font-size: 1.8rem;">Ready to Get Started?</h3>
              <p style="font-size: 1.1rem; opacity: 0.95; line-height: 1.6; margin-bottom: 30px;">Let's discuss which maintenance plan is right for your infrastructure and ensure your systems stay secure, performant, and up-to-date.</p>
              <p style="margin: 0; font-size: 1.05rem; opacity: 0.9;">Contact us to schedule a consultation and get a customized quote for your specific needs.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
useHead({
  title: 'Platform Maintenance Plans | Geek Cafe',
  meta: [
    { name: 'description', content: 'Proactive platform maintenance and infrastructure management. Choose from flexible plans or pay-as-you-go support for AWS, DevOps, and system updates.' }
  ]
})
</script>

<style scoped src="@/assets/css/service-detail-card.css"></style>
